import type { Workspace } from "schemas/generated/workspace";
import type { AgentAnalyticsContinuation } from "./AgentAnalyticsLaunchOverview";

import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { getAgentProjectLandingPath } from "@v2/utils/agentLanding";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

import { AgentLaunchModal } from "./AgentLaunchModal";

const AGENT_LAUNCH_FONT_URL =
  "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap";

type AgentLaunchContextValue = {
  isAvailable: boolean;
  openWelcome: (
    source: string,
    options?: {
      variant?: "analytics" | "default";
      analyticsContinuation?: AgentAnalyticsContinuation;
      onNotNow?: () => void;
    } & NewProjectOptions,
  ) => void;
  /** Creates the Agent project named after the workspace and opens it; no welcome, no naming. */
  launchProject: () => Promise<boolean>;
};

type NewProjectOptions = {
  /** Lands the new project on its Home tab with these params instead of the default landing. */
  homeSearchParams?: Record<string, string>;
  /** Links this v1 Shopify install to the new project, which also puts it on Shopify checkout. */
  shopifyIntegrationInstallationId?: string;
};

type WelcomeRequest = {
  source: string;
  variant: "analytics" | "default";
  analyticsContinuation: AgentAnalyticsContinuation;
  onNotNow?: () => void;
  newProjectOptions: NewProjectOptions;
};

type LaunchWorkspace = Pick<
  Workspace,
  "defaultProjectType" | "id" | "name" | "paymentProcessor"
>;

const AgentLaunchContext = React.createContext<
  AgentLaunchContextValue | undefined
>(undefined);

export function useAgentLaunch() {
  return React.useContext(AgentLaunchContext);
}

export function AgentLaunchProvider({ children }: React.PropsWithChildren) {
  const workspace = useCurrentWorkspace();
  if (!workspace) {
    return children;
  }

  return (
    <WorkspaceLaunchProvider key={workspace.id} workspace={workspace}>
      {children}
    </WorkspaceLaunchProvider>
  );
}

function WorkspaceLaunchProvider({
  workspace,
  children,
}: React.PropsWithChildren<{ workspace: LaunchWorkspace }>) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { createProject } = useOnboardingCreateProject();

  const [welcomeRequest, setWelcomeRequest] =
    React.useState<WelcomeRequest | null>(null);
  const [isNaming, setIsNaming] = React.useState(false);
  const [namingProjectOptions, setNamingProjectOptions] =
    React.useState<NewProjectOptions>({});
  const [projectName, setProjectName] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);

  const isAvailable =
    workspace.paymentProcessor !== "shopify" &&
    workspace.defaultProjectType !== "agent";

  const createAgentProject = React.useCallback(
    async (
      name: string,
      {
        homeSearchParams,
        shopifyIntegrationInstallationId,
      }: NewProjectOptions = {},
    ) => {
      if (isCreating || !name.trim() || !user) {
        return false;
      }
      setIsCreating(true);
      try {
        const result = await createProject({
          shopName: name.trim(),
          workspaceId: workspace.id,
          hostingType: "replo",
          shouldInvalidateUser: true,
          isAgentEnabled: true,
          shopifyIntegrationInstallationId,
        });
        const projectId = result.project.id;
        navigate(
          homeSearchParams
            ? `${generatePath(routes.agent.home, { projectId })}?${new URLSearchParams(homeSearchParams).toString()}`
            : getAgentProjectLandingPath({ projectId }),
          { replace: true },
        );
        return true;
      } catch {
        toast.error({
          title: "Couldn't create project",
          description: "Try again. If the problem continues, contact support.",
        });
        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [createProject, isCreating, navigate, user, workspace.id],
  );

  const contextValue: AgentLaunchContextValue = {
    isAvailable,
    openWelcome: (source, options) => {
      setWelcomeRequest({
        source,
        variant: options?.variant ?? "default",
        analyticsContinuation: options?.analyticsContinuation ?? "insights",
        onNotNow: options?.onNotNow,
        newProjectOptions: {
          homeSearchParams: options?.homeSearchParams,
          shopifyIntegrationInstallationId:
            options?.shopifyIntegrationInstallationId,
        },
      });
    },
    launchProject: () => createAgentProject(workspace.name),
  };

  return (
    <AgentLaunchContext.Provider value={contextValue}>
      <link rel="stylesheet" href={AGENT_LAUNCH_FONT_URL} />
      {children}
      <AgentLaunchModal
        open={welcomeRequest !== null}
        source={welcomeRequest?.source ?? "agent-launch"}
        variant={welcomeRequest?.variant ?? "default"}
        analyticsContinuation={
          welcomeRequest?.analyticsContinuation ?? "insights"
        }
        onEnterBeta={() => {
          setNamingProjectOptions(welcomeRequest?.newProjectOptions ?? {});
          setWelcomeRequest(null);
          setIsNaming(true);
        }}
        onDecline={(method) => {
          const onNotNow = welcomeRequest?.onNotNow;
          setWelcomeRequest(null);
          if (method === "not_now") {
            onNotNow?.();
          }
        }}
      />
      <ResponsiveDialog
        open={isNaming}
        onOpenChange={(open) => {
          if (!open) {
            setIsNaming(false);
          }
        }}
        title="Name your new Replo project"
        size="sm"
        footer={
          <Button
            type="submit"
            form="agent-launch-name-form"
            disabled={isCreating || !projectName.trim()}
            loading={isCreating}
            className="w-full"
          >
            Create project
          </Button>
        }
      >
        <form
          id="agent-launch-name-form"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void createAgentProject(projectName, namingProjectOptions);
          }}
        >
          <LabeledControl id="agent-project-name" label="Project" size="base">
            <Input
              id="agent-project-name"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              size="base"
              placeholder="Acme"
              autoFocus
              required
            />
          </LabeledControl>
        </form>
      </ResponsiveDialog>
    </AgentLaunchContext.Provider>
  );
}
